import { useState, useEffect, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import { X, FileText, Plus, Calendar, Clock, RefreshCcw, Eye, MessageSquare } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import { AsambleaRecord } from "../../../../api/asambleaService";
import {
  obtenerPreguntasAsamblea,
  PreguntaAsambleaRecord,
} from "../../../../api/preguntaAsambleaService";
import { HUB_BASE_URL } from "../../../../api/signalRConfig";
import DetailPreguntaAsambleaModal from "./DetailPreguntaAsambleaModal";
import CreatePreguntaAsambleaModal from "./CreatePreguntaAsambleaModal";
import RespuestasPreguntaAsambleaModal from "./RespuestasPreguntaAsambleaModal";
import Swal from "sweetalert2";

interface PreguntasAsambleaModalProps {
  isOpen: boolean;
  asamblea: AsambleaRecord | null;
  onClose: () => void;
}

const formatDate = (value: string) => {
  if (!value || value.trim() === "") return "Sin fecha";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const statusStyles: Record<
  string,
  { text: string; badge: string }
> = {
  D: {
    text: "Sin iniciar",
    badge: "text-gray-600 bg-gray-50 border border-gray-200",
  },
  A: {
    text: "Abierta",
    badge: "text-green-600 bg-green-50 border border-green-200",
  },
  C: {
    text: "Cerrada",
    badge: "text-red-600 bg-red-50 border border-red-200",
  },
  E: {
    text: "En curso",
    badge: "text-orange-600 bg-orange-50 border border-orange-200",
  },
};

const getStatusStyle = (estatus: string) => {
  const key = (estatus || "").trim().charAt(0).toUpperCase();
  return (
    statusStyles[key] || {
      text: estatus || "Sin estado",
      badge: "text-gray-600 bg-gray-50 border border-gray-200",
    }
  );
};

export default function PreguntasAsambleaModal({
  isOpen,
  asamblea,
  onClose,
}: PreguntasAsambleaModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [preguntas, setPreguntas] = useState<PreguntaAsambleaRecord[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedPregunta, setSelectedPregunta] = useState<PreguntaAsambleaRecord | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRespuestasModalOpen, setIsRespuestasModalOpen] = useState(false);
  const [preguntaForRespuestas, setPreguntaForRespuestas] = useState<PreguntaAsambleaRecord | null>(null);
  const fetchPreguntasRef = useRef<() => Promise<void>>(null!);



  useEffect(() => {
    if (!isOpen || !asamblea?.asa_codigo) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${HUB_BASE_URL}/preguntahub`, { withCredentials: true })
      .withAutomaticReconnect()
      .build();

    connection.on("cargarpPreguntasDeAsamblea", () => {
      fetchPreguntasRef.current?.();
    });

    const start = async () => {
      try {
        await connection.start();
        await connection.invoke("UnirseAGrupo", asamblea.asa_codigo.toString());
      } catch (err) {
        if (err instanceof Error && process.env.NODE_ENV === "development") {
          console.error("[PreguntaHub]", err.message);
        }
      }
    };

    start();

    return () => {
      if (connection.state !== signalR.HubConnectionState.Disconnected) {
        connection.stop();
      }
    };
  }, [isOpen, asamblea?.asa_codigo]);

  const fetchPreguntas = useCallback(async () => {
    if (!asamblea?.asa_codigo) return;

    setIsLoading(true);
    try {
      const response = await obtenerPreguntasAsamblea({
        asamblea_codigo: String(asamblea.asa_codigo),
        codigo: "",
      });

      if (response.code === "1" && Array.isArray(response.object_)) {
        setPreguntas(response.object_);
      } else {
        setPreguntas([]);
      }
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error al cargar preguntas:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las preguntas",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [asamblea?.asa_codigo]);

  useEffect(() => {
    if (isOpen && asamblea) {
      fetchPreguntas();
    } else {
      setPreguntas([]);
    }
  }, [isOpen, asamblea, fetchPreguntas]);

  fetchPreguntasRef.current = fetchPreguntas;

  const handleCreatePregunta = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = (created: boolean) => {
    setIsCreateModalOpen(false);
    if (created) {
      fetchPreguntas();
    }
  };

  const handlePreguntaClick = (pregunta: PreguntaAsambleaRecord) => {
    setSelectedPregunta(pregunta);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = (shouldReload?: boolean) => {
    setIsDetailModalOpen(false);
    setSelectedPregunta(null);
    if (shouldReload) {
      fetchPreguntas();
    }
  };

  const handleRespuestasClick = (e: React.MouseEvent, pregunta: PreguntaAsambleaRecord) => {
    e.stopPropagation();
    setPreguntaForRespuestas(pregunta);
    setIsRespuestasModalOpen(true);
  };

  const handleCloseRespuestasModal = () => {
    setIsRespuestasModalOpen(false);
    setPreguntaForRespuestas(null);
  };

  if (!isOpen || !asamblea) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-military-600 flex items-center gap-2">
              <FileText size={28} />
              Preguntas de la Asamblea
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Asamblea: {asamblea.asa_titulo}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              <span className="font-semibold">Llave:</span> <span className="font-bold text-black text-lg font-mono">{asamblea.asa_llave}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {lastUpdated && (
                <span className="text-xs text-gray-500">
                  Actualizado: {lastUpdated.toLocaleString()}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                className="gap-2 bg-military-500 hover:bg-military-600 text-white"
                onClick={handleCreatePregunta}
              >
                <Plus size={18} />
                Nueva Pregunta
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={fetchPreguntas}
                disabled={isLoading}
              >
                <RefreshCcw
                  size={18}
                  className={isLoading ? "animate-spin" : ""}
                />
                Actualizar
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
              <p className="text-gray-600">Cargando preguntas...</p>
            </div>
          ) : preguntas.length === 0 ? (
            <div className="text-center py-16">
              <FileText
                size={48}
                className="mx-auto text-gray-400 mb-4"
              />
              <p className="text-gray-600 font-medium">
                No se encontraron preguntas
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Crea una nueva pregunta para esta asamblea.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {preguntas.map((pregunta) => {
                const style = getStatusStyle(pregunta.pra_estatus);
                return (
                  <Card key={pregunta.pra_codigo} className="shadow-sm hover:shadow-md transition">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-semibold text-gray-500">
                              #{pregunta.pra_codigo}
                            </span>
                            <span
                              className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${style.badge}`}
                            >
                              {pregunta.pra_estatus_nombre || style.text}
                            </span>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {pregunta.pra_texto}
                          </h3>
                          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              Apertura: {formatDate(pregunta.pra_fecha_apertura || "")}
                            </span>
                            {pregunta.pra_fecha_inicio && (
                              <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                Inicio: {formatDate(pregunta.pra_fecha_inicio)}
                              </span>
                            )}
                            {pregunta.pra_fecha_cierre && (
                              <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                Cierre: {formatDate(pregunta.pra_fecha_cierre)}
                              </span>
                            )}
                            {pregunta.pra_con_tiempo === 1 && pregunta.pra_minutos && (
                              <span className="flex items-center gap-1">
                                <Clock size={14} />
                                Tiempo: {pregunta.pra_minutos} minutos
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handlePreguntaClick(pregunta)}
                            variant="outline"
                            className="border-military-500 text-military-600 hover:bg-military-50"
                          >
                            <Eye size={16} className="mr-2" />
                            Ver Detalle
                          </Button>
                          <Button
                            onClick={(e) => handleRespuestasClick(e, pregunta)}
                            variant="outline"
                            className="border-blue-500 text-blue-600 hover:bg-blue-50"
                          >
                            <MessageSquare size={16} className="mr-2" />
                            Respuestas
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        <DetailPreguntaAsambleaModal
          isOpen={isDetailModalOpen}
          pregunta={selectedPregunta}
          asamblea={asamblea}
          onClose={handleCloseDetailModal}
        />

        <CreatePreguntaAsambleaModal
          isOpen={isCreateModalOpen}
          asamblea={asamblea}
          onClose={handleCloseCreateModal}
        />

        <RespuestasPreguntaAsambleaModal
          isOpen={isRespuestasModalOpen}
          pregunta={preguntaForRespuestas}
          onClose={handleCloseRespuestasModal}
        />
      </div>
    </div>
  );
}
